import {
  Sparkles, Smile, Meh, Frown, Angry,
  CheckCircle2, XCircle, AlertCircle, MinusCircle,
} from 'lucide-react';

interface AiAnalysisCardProps {
  sentiment?: string | null;
  aiSentiment?: string | null;
  categoryReasoning?: string | null;
  interestLevel?: string | null;
  interestLevelReasoning?: string | null;
  resolution?: string | null;
  resolutionReasoning?: string | null;
}

const sentimentIcons = new Map<string, React.ReactNode>();
sentimentIcons.set('Positive', <Smile className="h-5 w-5 text-green-500" />);
sentimentIcons.set('Neutral', <Meh className="h-5 w-5 text-gray-400" />);
sentimentIcons.set('Negative', <Frown className="h-5 w-5 text-orange-500" />);
sentimentIcons.set('Escalating', <Angry className="h-5 w-5 text-red-500" />);

const resolutionIcons = new Map<string, React.ReactNode>();
resolutionIcons.set('Resolved', <CheckCircle2 className="h-5 w-5 text-green-500" />);
resolutionIcons.set('Partially Resolved', <AlertCircle className="h-5 w-5 text-yellow-500" />);
resolutionIcons.set('Failed', <XCircle className="h-5 w-5 text-red-500" />);
resolutionIcons.set('Unresolved', <MinusCircle className="h-5 w-5 text-gray-400" />);

const interestColors = new Map<string, string>([
  ['Not Interesting', 'bg-gray-300'],
  ['Somewhat Interesting', 'bg-blue-400'],
  ['Interesting', 'bg-yellow-500'],
  ['Very Interesting', 'bg-red-500'],
]);

const interestCounts = new Map<string, number>([
  ['Not Interesting', 1],
  ['Somewhat Interesting', 2],
  ['Interesting', 3],
  ['Very Interesting', 4],
]);

function AnalysisRow({
  label,
  icon,
  value,
  reasoning,
}: {
  label: string;
  icon?: React.ReactNode;
  value?: string | null;
  reasoning?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium">{label}:</span>
        <span className="text-sm">{value}</span>
      </div>
      {reasoning && (
        <p className="text-xs text-muted-foreground ml-7">
          {reasoning}
        </p>
      )}
    </div>
  );
}

export function AiAnalysisCard({
  sentiment,
  aiSentiment,
  categoryReasoning,
  interestLevel,
  interestLevelReasoning,
  resolution,
  resolutionReasoning,
}: AiAnalysisCardProps) {
  const hasAny = sentiment || interestLevel || resolution
    || categoryReasoning;
  if (!hasAny) return null;

  const count = interestLevel
    ? (interestCounts.get(interestLevel) ?? 0) : 0;
  const barColor = interestLevel
    ? (interestColors.get(interestLevel) ?? 'bg-gray-300') : 'bg-gray-300';

  const interestIcon = interestLevel ? (
    <div className="flex items-end gap-0.5 h-4">
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className={`w-1 rounded-sm ${i <= count ? barColor : 'bg-gray-200'}`}
          style={{ height: `${i * 25}%` }}
        />
      ))}
    </div>
  ) : undefined;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-purple-500" />
        <h4 className="font-semibold">AI Analysis</h4>
      </div>
      <div className="space-y-3">
        {categoryReasoning && (
          <div className="space-y-1">
            <span className="text-sm font-medium">
              Category Reasoning:
            </span>
            <p className="text-xs text-muted-foreground">
              {categoryReasoning}
            </p>
          </div>
        )}
        <AnalysisRow
          label="Sentiment"
          icon={sentiment ? sentimentIcons.get(sentiment) : undefined}
          value={sentiment}
          reasoning={aiSentiment}
        />
        <AnalysisRow
          label="Interest Level"
          icon={interestIcon}
          value={interestLevel}
          reasoning={interestLevelReasoning}
        />
        <AnalysisRow
          label="Resolution"
          icon={resolution ? resolutionIcons.get(resolution) : undefined}
          value={resolution}
          reasoning={resolutionReasoning}
        />
      </div>
    </div>
  );
}
